import { useId } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Switch.css";

export interface SwitchProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  "onChange"
> {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** Visible text tied to the control with `aria-labelledby`. */
  label?: ReactNode;
  /** Which side the visible label sits on. Defaults to `end`. */
  labelPosition?: "start" | "end";
}

/**
 * An accessible toggle. Built on `<button role="switch" aria-checked>` rather
 * than a checkbox because the change is applied immediately, with no submit.
 * Supply either `label` or an `aria-label`.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  label,
  labelPosition = "end",
  className,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: SwitchProps) {
  const uid = useId();
  const labelId = `${uid}label`;
  const hasLabel = label !== undefined && label !== null;

  const toggle = () => {
    if (disabled) return;
    onCheckedChange?.(!checked);
  };

  const classes = [
    "fc-switch",
    `fc-switch--label-${labelPosition}`,
    checked ? "is-checked" : null,
    disabled ? "is-disabled" : null,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  const labelNode = hasLabel ? (
    <span className="fc-switch__label" id={labelId} onClick={toggle}>
      {label}
    </span>
  ) : null;

  return (
    <span {...rest} className={classes}>
      {labelPosition === "start" ? labelNode : null}
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy ?? (hasLabel ? labelId : undefined)}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        className="fc-switch__track"
        onClick={toggle}
      >
        <span className="fc-switch__knob" aria-hidden="true" />
      </button>
      {labelPosition === "end" ? labelNode : null}
    </span>
  );
}
